<template>
	<div class="target-editor">
		<div class="target-toolbar" role="toolbar" aria-label="Image target tools">
			<button
				type="button"
				class="btn btn-sm"
				:class="tool === 'tap-mask' ? 'btn-warning' : 'btn-outline-warning'"
				@click="setTool('tap-mask')">
				<i class="bi bi-cursor-fill"></i>
				Tap mask
			</button>
			<button
				type="button"
				class="btn btn-sm"
				:class="tool === 'draw-mask' ? 'btn-warning' : 'btn-outline-warning'"
				@click="setTool('draw-mask')">
				<i class="bi bi-brush"></i>
				Draw mask
			</button>
			<button
				type="button"
				class="btn btn-sm"
				:class="tool === 'box' ? 'btn-warning' : 'btn-outline-warning'"
				@click="setTool('box')">
				<i class="bi bi-bounding-box"></i>
				Box
			</button>
			<button
				type="button"
				class="btn btn-sm"
				:class="tool === 'draw-circle' ? 'btn-warning' : 'btn-outline-warning'"
				@click="setTool('draw-circle')">
				<i class="bi bi-circle"></i>
				Draw circle
			</button>
			<button
				type="button"
				class="btn btn-sm btn-outline-light"
				title="How targeting works"
				aria-label="How targeting works"
				@click="showTargetHelp">
				<i class="bi bi-question-circle"></i>
			</button>
		</div>

		<div class="target-controls">
			<label class="brush-control">
				<span>Brush</span>
				<input
					v-model.number="brushSize"
					type="range"
					min="8"
					max="72"
					step="2" />
			</label>
			<button
				type="button"
				class="btn btn-sm"
				:class="eraseMode ? 'btn-light' : 'btn-outline-light'"
				@click="eraseMode = !eraseMode">
				<i class="bi bi-eraser"></i>
				Erase
			</button>
			<button
				type="button"
				class="btn btn-sm btn-outline-light"
				:disabled="!canUndo"
				@click="undo">
				<i class="bi bi-arrow-counterclockwise"></i>
				Undo
			</button>
			<button type="button" class="btn btn-sm btn-outline-light" @click="clearTarget">
				<i class="bi bi-x-circle"></i>
				Clear
			</button>
		</div>

		<div
			ref="stageRef"
			class="target-stage"
			:class="{ busy: isMasking }"
			@pointerdown="handlePointerDown"
			@pointermove="handlePointerMove"
			@pointerup="handlePointerUp"
			@pointercancel="handlePointerUp"
			@pointerleave="handlePointerLeave">
			<img
				ref="imageRef"
				:src="imageSrc"
				alt="Selected meal"
				draggable="false"
				@load="syncCanvas" />
			<canvas ref="overlayRef" class="target-overlay"></canvas>
			<div v-if="isMasking" class="target-status">Finding object...</div>
		</div>

		<div v-if="statusMessage" class="target-message">
			{{ statusMessage }}
		</div>

		<div class="target-actions">
			<button type="button" class="btn btn-outline-light" @click="emitCancel">
				Cancel
			</button>
			<button type="button" class="btn btn-success" @click="confirmTarget">
				Use target
			</button>
		</div>
	</div>
</template>

<script setup>
	import { computed, nextTick, onBeforeUnmount, ref, watch } from "vue"
	import Swal from "sweetalert2"

	const props = defineProps({
		imageSrc: { type: String, required: true },
	})

	const emit = defineEmits(["confirm", "cancel"])

	const tool = ref("tap-mask")
	const brushSize = ref(28)
	const eraseMode = ref(false)
	const isMasking = ref(false)
	const statusMessage = ref("")

	const imageRef = ref(null)
	const overlayRef = ref(null)
	const stageRef = ref(null)
	const imageInfo = ref({ width: 0, height: 0, displayWidth: 0, displayHeight: 0 })
	const currentTarget = ref(null)
	const targetParts = ref([])
	const history = ref([])
	const canUndo = computed(() => history.value.length > 0)

	let maskCanvas = document.createElement("canvas")
	let drawing = false
	let activePointerId = null
	let startPoint = null
	let lastPoint = null
	let freehandPath = []
	let resizeObserver = null
	let segmenterPromise = null

	function snapshotState() {
		const snapshot = document.createElement("canvas")
		snapshot.width = maskCanvas.width
		snapshot.height = maskCanvas.height
		snapshot.getContext("2d")?.drawImage(maskCanvas, 0, 0)
		return {
			mask: snapshot,
			target: currentTarget.value ? JSON.parse(JSON.stringify(currentTarget.value)) : null,
			parts: targetParts.value.map((part) => JSON.parse(JSON.stringify(part))),
			path: freehandPath.map((point) => ({ ...point })),
		}
	}

	function pushHistory() {
		history.value.push(snapshotState())
		if (history.value.length > 25) history.value.shift()
	}

	function restoreState(state) {
		const ctx = maskCanvas.getContext("2d")
		ctx?.clearRect(0, 0, maskCanvas.width, maskCanvas.height)
		ctx?.drawImage(state.mask, 0, 0)
		currentTarget.value = state.target
		targetParts.value = state.parts || []
		freehandPath = state.path.map((point) => ({ ...point }))
		drawOverlay()
	}

	function undo() {
		const state = history.value.pop()
		if (!state) return
		restoreState(state)
	}

	function setTool(nextTool) {
		tool.value = nextTool
		eraseMode.value = false
	}

	function showTargetHelp() {
		Swal.fire({
			icon: "info",
			title: "Selecting targets",
			html: `
				<div class="text-start">
					<p>Select only the food or drink the AI should identify. Everything outside the selected target is ignored.</p>
					<p><strong>Tap mask</strong> selects an object you tap. <strong>Draw mask</strong> paints the area you want. <strong>Box</strong> adds a rectangle. <strong>Draw circle</strong> lets you draw a loop around an item.</p>
					<p>You can add multiple targets. Turn on <strong>Erase</strong> and tap a highlighted target to delete it. Use <strong>Undo</strong> or <strong>Clear</strong> if needed.</p>
				</div>
			`,
			confirmButtonText: "Got it",
		})
	}

	function syncCanvas() {
		const img = imageRef.value
		const overlay = overlayRef.value
		if (!img || !overlay) return

		const rect = img.getBoundingClientRect()
		const scale = window.devicePixelRatio || 1
		overlay.style.width = `${rect.width}px`
		overlay.style.height = `${rect.height}px`
		overlay.width = Math.max(1, Math.round(rect.width * scale))
		overlay.height = Math.max(1, Math.round(rect.height * scale))

		imageInfo.value = {
			width: img.naturalWidth,
			height: img.naturalHeight,
			displayWidth: rect.width,
			displayHeight: rect.height,
		}

		if (maskCanvas.width !== img.naturalWidth || maskCanvas.height !== img.naturalHeight) {
			maskCanvas = document.createElement("canvas")
			maskCanvas.width = img.naturalWidth
			maskCanvas.height = img.naturalHeight
		}

		drawOverlay()
	}

	function stagePoint(event) {
		const img = imageRef.value
		if (!img || !imageInfo.value.width || !imageInfo.value.height) return null
		const rect = img.getBoundingClientRect()
		const x = Math.min(Math.max(event.clientX - rect.left, 0), rect.width)
		const y = Math.min(Math.max(event.clientY - rect.top, 0), rect.height)
		return {
			x,
			y,
			nx: x / Math.max(rect.width, 1),
			ny: y / Math.max(rect.height, 1),
			ix: (x / Math.max(rect.width, 1)) * imageInfo.value.width,
			iy: (y / Math.max(rect.height, 1)) * imageInfo.value.height,
		}
	}

	async function handlePointerDown(event) {
		if (isMasking.value) return
		const point = stagePoint(event)
		if (!point) return

		event.preventDefault()
		event.currentTarget.setPointerCapture?.(event.pointerId)
		activePointerId = event.pointerId
		drawing = true
		startPoint = point
		lastPoint = point
		freehandPath = [point]

		if (eraseMode.value) {
			drawing = false
			activePointerId = null
			const seed = findMaskSeed(point)
			if (!seed) {
				statusMessage.value = "Tap a highlighted target to remove it."
				drawOverlay()
				return
			}
			pushHistory()
			removeMaskIsland(seed)
			updateTargetAfterMaskEdit()
			statusMessage.value = ""
			drawOverlay()
			return
		}

		if (tool.value === "tap-mask") {
			drawing = false
			pushHistory()
			await runTapMask(point)
			return
		}

		if (tool.value === "draw-mask") {
			pushHistory()
			paintMask(point, point)
			currentTarget.value = {
				mode: "draw-mask",
				region: { bounds: normalizedMaskBounds() },
			}
		}

		drawOverlay()
	}

	function handlePointerMove(event) {
		if (!drawing || event.pointerId !== activePointerId || isMasking.value) return
		const point = stagePoint(event)
		if (!point) return
		event.preventDefault()

		if (eraseMode.value) return

		if (tool.value === "draw-mask") {
			paintMask(lastPoint || point, point)
			lastPoint = point
			updateCurrentTarget("draw-mask")
		} else if (tool.value === "box") {
			if (!startPoint?.historyPushed) {
				pushHistory()
				startPoint.historyPushed = true
			}
			currentTarget.value = {
				mode: "box",
				region: normalizedBox(startPoint, point),
			}
		} else if (tool.value === "draw-circle") {
			if (!startPoint?.historyPushed) {
				pushHistory()
				startPoint.historyPushed = true
			}
			freehandPath.push(point)
			currentTarget.value = {
				mode: "draw-circle",
				region: {
					path: normalizedPath(freehandPath),
					bounds: normalizedPathBounds(freehandPath),
				},
			}
		}

		drawOverlay()
	}

	function handlePointerUp(event) {
		if (!drawing || event.pointerId !== activePointerId) return
		drawing = false
		activePointerId = null

		const point = stagePoint(event) || lastPoint || startPoint
		if (eraseMode.value) {
			drawOverlay()
			return
		}

		if (tool.value === "draw-mask") {
			addTargetPart("draw-mask", { bounds: normalizedMaskBounds() })
		}

		if (tool.value === "box" && startPoint && point) {
			commitBox(startPoint, point)
		}

		if (tool.value === "draw-circle" && freehandPath.length > 2) {
			commitCirclePath()
		}

		drawOverlay()
	}

	function handlePointerLeave(event) {
		if (event.buttons === 0) handlePointerUp(event)
	}

	function paintMask(from, to) {
		const ctx = maskCanvas.getContext("2d")
		if (!ctx) return

		ctx.save()
		ctx.lineCap = "round"
		ctx.lineJoin = "round"
		ctx.lineWidth = brushSize.value * (imageInfo.value.width / Math.max(imageInfo.value.displayWidth, 1))
		ctx.globalCompositeOperation = eraseMode.value ? "destination-out" : "source-over"
		ctx.strokeStyle = "rgba(255,255,255,1)"
		ctx.beginPath()
		ctx.moveTo(from.ix, from.iy)
		ctx.lineTo(to.ix, to.iy)
		ctx.stroke()
		if (from.ix === to.ix && from.iy === to.iy) {
			ctx.beginPath()
			ctx.arc(to.ix, to.iy, ctx.lineWidth / 2, 0, Math.PI * 2)
			ctx.fillStyle = "rgba(255,255,255,1)"
			ctx.fill()
		}
		ctx.restore()
	}

	function findMaskSeed(point) {
		const ctx = maskCanvas.getContext("2d")
		if (!ctx || !maskCanvas.width || !maskCanvas.height) return null

		const width = maskCanvas.width
		const height = maskCanvas.height
		const imageData = ctx.getImageData(0, 0, width, height)
		const data = imageData.data
		const centerX = clamp(Math.round(point.ix), 0, width - 1)
		const centerY = clamp(Math.round(point.iy), 0, height - 1)
		const centerIndex = (centerY * width + centerX) * 4 + 3
		if (data[centerIndex] > 0) return { x: centerX, y: centerY }

		const radius = Math.max(
			6,
			Math.round(brushSize.value * (imageInfo.value.width / Math.max(imageInfo.value.displayWidth, 1)))
		)
		for (let r = 1; r <= radius; r++) {
			for (let y = Math.max(0, centerY - r); y <= Math.min(height - 1, centerY + r); y++) {
				for (let x = Math.max(0, centerX - r); x <= Math.min(width - 1, centerX + r); x++) {
					if (Math.abs(x - centerX) !== r && Math.abs(y - centerY) !== r) continue
					if (data[(y * width + x) * 4 + 3] > 0) return { x, y }
				}
			}
		}

		return null
	}

	function removeMaskIsland(seed) {
		const ctx = maskCanvas.getContext("2d")
		if (!ctx || !maskCanvas.width || !maskCanvas.height) return

		const width = maskCanvas.width
		const height = maskCanvas.height
		const imageData = ctx.getImageData(0, 0, width, height)
		const data = imageData.data
		const visited = new Uint8Array(width * height)
		const stack = [seed.y * width + seed.x]

		while (stack.length) {
			const index = stack.pop()
			if (visited[index]) continue
			visited[index] = 1

			const pixelOffset = index * 4
			if (data[pixelOffset + 3] === 0) continue

			data[pixelOffset] = 0
			data[pixelOffset + 1] = 0
			data[pixelOffset + 2] = 0
			data[pixelOffset + 3] = 0

			const x = index % width
			const y = Math.floor(index / width)
			if (x > 0) stack.push(index - 1)
			if (x < width - 1) stack.push(index + 1)
			if (y > 0) stack.push(index - width)
			if (y < height - 1) stack.push(index + width)
		}

		ctx.putImageData(imageData, 0, 0)
	}

	function updateTargetAfterMaskEdit() {
		const bounds = normalizedMaskBounds()
		targetParts.value = bounds ? [{ mode: "draw-mask", region: { bounds } }] : []
		updateCurrentTarget("draw-mask")
	}

	async function runTapMask(point) {
		statusMessage.value = ""
		isMasking.value = true
		try {
			const segmenter = await loadSegmenter()
			const img = imageRef.value
			if (!segmenter || !img) throw new Error("Automask is not available.")

			await new Promise((resolve, reject) => {
				segmenter.segment(
					img,
					{ keypoint: { x: point.nx, y: point.ny } },
					(result) => {
						try {
							applySegmentationResult(result)
							addTargetPart("tap-mask", {
								tap: { x: point.nx, y: point.ny },
								bounds: normalizedMaskBounds(),
							})
							resolve()
						} catch (err) {
							reject(err)
						}
					}
				)
			})
		} catch (err) {
			statusMessage.value =
				err?.message || "Tap mask could not load. Draw a mask, box, or circle instead."
		} finally {
			isMasking.value = false
			drawOverlay()
		}
	}

	async function loadSegmenter() {
		if (!segmenterPromise) {
			segmenterPromise = import("@mediapipe/tasks-vision").then(
				async ({ FilesetResolver, InteractiveSegmenterLegacy }) => {
					const vision = await FilesetResolver.forVisionTasks(
						"https://cdn.jsdelivr.net/npm/@mediapipe/tasks-vision@1.0.1/wasm"
					)
					return InteractiveSegmenterLegacy.createFromOptions(vision, {
						baseOptions: {
							modelAssetPath:
								"https://storage.googleapis.com/mediapipe-models/interactive_segmenter/magic_touch/float32/1/magic_touch.tflite",
						},
						outputCategoryMask: false,
						outputConfidenceMasks: true,
					})
				}
			)
			segmenterPromise.catch(() => {
				segmenterPromise = null
			})
		}
		return segmenterPromise
	}

	function applySegmentationResult(result) {
		const mask = result?.categoryMask || result?.confidenceMasks?.[0]
		if (!mask) throw new Error("No object mask was returned.")

		const raw =
			mask.getAsUint8Array?.() ||
			mask.getAsFloat32Array?.() ||
			mask.getAsWebGLTexture?.()
		if (!raw || typeof raw.length !== "number") {
			throw new Error("Unsupported automask result.")
		}

		const width = mask.width || imageInfo.value.width
		const height = mask.height || imageInfo.value.height
		const source = document.createElement("canvas")
		source.width = width
		source.height = height
		const ctx = source.getContext("2d")
		const imageData = ctx.createImageData(width, height)

		for (let i = 0; i < raw.length; i++) {
			const value = raw[i]
			const alpha = value > 0.45 || value === 1 ? 255 : 0
			const offset = i * 4
			imageData.data[offset] = 255
			imageData.data[offset + 1] = 255
			imageData.data[offset + 2] = 255
			imageData.data[offset + 3] = alpha
		}

		ctx.putImageData(imageData, 0, 0)
		const targetCtx = maskCanvas.getContext("2d")
		targetCtx.globalCompositeOperation = "source-over"
		targetCtx.drawImage(source, 0, 0, maskCanvas.width, maskCanvas.height)
	}

	function commitBox(start, end) {
		const ctx = maskCanvas.getContext("2d")
		if (!ctx) return
		const box = normalizedBox(start, end)
		const crop = normalizedRegionToPixels(box, maskCanvas.width, maskCanvas.height)
		ctx.save()
		ctx.fillStyle = "rgba(255,255,255,1)"
		ctx.fillRect(crop.x, crop.y, crop.width, crop.height)
		ctx.restore()
		addTargetPart("box", box)
	}

	function commitCirclePath() {
		const ctx = maskCanvas.getContext("2d")
		if (!ctx || freehandPath.length < 3) return

		ctx.save()
		ctx.fillStyle = "rgba(255,255,255,1)"
		ctx.beginPath()
		ctx.moveTo(freehandPath[0].ix, freehandPath[0].iy)
		for (const point of freehandPath.slice(1)) {
			ctx.lineTo(point.ix, point.iy)
		}
		ctx.closePath()
		ctx.fill()
		ctx.restore()

		addTargetPart("draw-circle", {
			path: normalizedPath(freehandPath),
			bounds: normalizedMaskBounds(),
		})
	}

	function addTargetPart(mode, region) {
		if (!normalizedMaskBounds()) {
			updateCurrentTarget(mode)
			return
		}
		targetParts.value.push({ mode, region })
		updateCurrentTarget(mode)
	}

	function updateCurrentTarget(preferredMode = null) {
		const bounds = normalizedMaskBounds()
		if (!bounds) {
			currentTarget.value = null
			targetParts.value = []
			return
		}

		const parts = targetParts.value.map((part) => JSON.parse(JSON.stringify(part)))
		currentTarget.value = {
			mode: parts.length > 1 ? "multi-target" : preferredMode || parts[0]?.mode || "draw-mask",
			region: {
				bounds,
				parts,
			},
		}
	}

	function normalizedBox(a, b) {
		const left = Math.min(a.nx, b.nx)
		const top = Math.min(a.ny, b.ny)
		const right = Math.max(a.nx, b.nx)
		const bottom = Math.max(a.ny, b.ny)
		return {
			x: left,
			y: top,
			width: right - left,
			height: bottom - top,
		}
	}

	function normalizedPath(path) {
		return path.map((point) => ({
			x: round(point.nx),
			y: round(point.ny),
		}))
	}

	function normalizedPathBounds(path) {
		if (!path.length) return null
		const xs = path.map((point) => point.nx)
		const ys = path.map((point) => point.ny)
		return {
			x: Math.min(...xs),
			y: Math.min(...ys),
			width: Math.max(...xs) - Math.min(...xs),
			height: Math.max(...ys) - Math.min(...ys),
		}
	}

	function normalizedMaskBounds() {
		const bounds = maskBounds()
		if (!bounds) return null
		return {
			x: round(bounds.x / imageInfo.value.width),
			y: round(bounds.y / imageInfo.value.height),
			width: round(bounds.width / imageInfo.value.width),
			height: round(bounds.height / imageInfo.value.height),
		}
	}

	function maskBounds() {
		const ctx = maskCanvas.getContext("2d")
		if (!ctx || !maskCanvas.width || !maskCanvas.height) return null
		const data = ctx.getImageData(0, 0, maskCanvas.width, maskCanvas.height).data
		let minX = maskCanvas.width
		let minY = maskCanvas.height
		let maxX = -1
		let maxY = -1

		for (let y = 0; y < maskCanvas.height; y++) {
			for (let x = 0; x < maskCanvas.width; x++) {
				if (data[(y * maskCanvas.width + x) * 4 + 3] > 0) {
					minX = Math.min(minX, x)
					minY = Math.min(minY, y)
					maxX = Math.max(maxX, x)
					maxY = Math.max(maxY, y)
				}
			}
		}

		if (maxX < 0 || maxY < 0) return null
		const padding = Math.round(Math.max(maskCanvas.width, maskCanvas.height) * 0.035)
		minX = Math.max(0, minX - padding)
		minY = Math.max(0, minY - padding)
		maxX = Math.min(maskCanvas.width - 1, maxX + padding)
		maxY = Math.min(maskCanvas.height - 1, maxY + padding)

		return {
			x: minX,
			y: minY,
			width: maxX - minX + 1,
			height: maxY - minY + 1,
		}
	}

	async function confirmTarget() {
		const target = currentTarget.value
		const targetImageSrc = await buildTargetImage(target)

		emit("confirm", {
			imageSrc: props.imageSrc,
			targetImageSrc,
			targetMode: target?.mode || null,
			targetRegion: target?.region || null,
		})
	}

	async function buildTargetImage(target) {
		if (!target) return null

		const img = await loadImage(props.imageSrc)
		const source = document.createElement("canvas")
		source.width = img.naturalWidth || img.width
		source.height = img.naturalHeight || img.height
		const sourceCtx = source.getContext("2d")
		sourceCtx.drawImage(img, 0, 0, source.width, source.height)

		const bounds = maskBounds()
		if (!bounds) return null
		return cropCanvas(source, bounds, maskCanvas)
	}

	function normalizedRegionToPixels(region, width, height) {
		const minSize = 12
		return {
			x: Math.max(0, Math.round(region.x * width)),
			y: Math.max(0, Math.round(region.y * height)),
			width: Math.max(minSize, Math.round(region.width * width)),
			height: Math.max(minSize, Math.round(region.height * height)),
		}
	}

	function cropCanvas(source, crop, mask) {
		const canvas = document.createElement("canvas")
		canvas.width = Math.min(crop.width, source.width - crop.x)
		canvas.height = Math.min(crop.height, source.height - crop.y)
		const ctx = canvas.getContext("2d")
		if (!mask) {
			ctx.fillStyle = "#ffffff"
			ctx.fillRect(0, 0, canvas.width, canvas.height)
		}
		ctx.drawImage(
			source,
			crop.x,
			crop.y,
			canvas.width,
			canvas.height,
			0,
			0,
			canvas.width,
			canvas.height
		)

		if (mask) {
			ctx.globalCompositeOperation = "destination-in"
			ctx.drawImage(
				mask,
				crop.x,
				crop.y,
				canvas.width,
				canvas.height,
				0,
				0,
				canvas.width,
				canvas.height
			)
			ctx.globalCompositeOperation = "source-over"
		}

		return mask
			? canvas.toDataURL("image/png")
			: canvas.toDataURL("image/jpeg", 0.92)
	}

	function drawOverlay() {
		const overlay = overlayRef.value
		if (!overlay) return
		const ctx = overlay.getContext("2d")
		const scale = window.devicePixelRatio || 1
		ctx.clearRect(0, 0, overlay.width, overlay.height)
		ctx.save()
		ctx.scale(scale, scale)

		const displayWidth = imageInfo.value.displayWidth
		const displayHeight = imageInfo.value.displayHeight

		const target = currentTarget.value
		if (target?.mode === "box" && target.region) {
			const box = typeof target.region.width === "number" ? target.region : null
			ctx.strokeStyle = "#fbbf24"
			ctx.lineWidth = 3
			ctx.setLineDash([8, 6])
			if (box) {
				ctx.strokeRect(
					box.x * displayWidth,
					box.y * displayHeight,
					box.width * displayWidth,
					box.height * displayHeight
				)
			}
		}

		if (target?.mode === "draw-circle" && freehandPath.length > 1 && drawing) {
			ctx.strokeStyle = "#38bdf8"
			ctx.lineWidth = 3
			ctx.beginPath()
			ctx.moveTo(freehandPath[0].x, freehandPath[0].y)
			for (const point of freehandPath.slice(1)) ctx.lineTo(point.x, point.y)
			ctx.stroke()
		}

		if (hasMask()) {
			ctx.globalAlpha = 0.38
			ctx.drawImage(maskCanvas, 0, 0, displayWidth, displayHeight)
			ctx.globalAlpha = 1
		}

		ctx.restore()
	}

	function hasMask() {
		return !!maskBounds()
	}

	function clearTarget(recordHistory = true) {
		if (recordHistory) pushHistory()
		const ctx = maskCanvas.getContext("2d")
		ctx?.clearRect(0, 0, maskCanvas.width, maskCanvas.height)
		currentTarget.value = null
		targetParts.value = []
		statusMessage.value = ""
		freehandPath = []
		drawOverlay()
	}

	function emitCancel() {
		emit("cancel")
	}

	function loadImage(src) {
		return new Promise((resolve, reject) => {
			const img = new Image()
			img.onload = () => resolve(img)
			img.onerror = reject
			img.src = src
		})
	}

	function round(value) {
		return Math.round(value * 10000) / 10000
	}

	function clamp(value, min, max) {
		return Math.min(Math.max(value, min), max)
	}

	watch(
		() => props.imageSrc,
		async () => {
			clearTarget(false)
			await nextTick()
			syncCanvas()
		}
	)

	nextTick(() => {
		syncCanvas()
		if (stageRef.value && "ResizeObserver" in window) {
			resizeObserver = new ResizeObserver(syncCanvas)
			resizeObserver.observe(stageRef.value)
		}
	})

	onBeforeUnmount(() => {
		resizeObserver?.disconnect()
		segmenterPromise?.then((segmenter) => segmenter.close()).catch(() => {})
		segmenterPromise = null
	})
</script>

<style lang="less" scoped>
	.target-editor {
		display: grid;
		grid-template-rows: auto auto minmax(0, 1fr) auto auto;
		gap: 0.75rem;
		padding: 0.75rem;
		border: 1px solid rgba(255, 255, 255, 0.18);
		border-radius: 8px;
		background: rgba(0, 0, 0, 0.28);
		max-height: calc(100dvh - 2rem);
		overflow: hidden;
	}

	.target-toolbar,
	.target-controls,
	.target-actions {
		display: flex;
		flex-wrap: wrap;
		gap: 0.5rem;
		align-items: center;
	}

	.target-actions {
		justify-content: flex-end;
	}

	.brush-control {
		display: inline-flex;
		align-items: center;
		gap: 0.5rem;
		color: #fff;
		font-size: 0.9rem;
	}

	.brush-control input {
		width: min(14rem, 52vw);
	}

	.target-stage {
		position: relative;
		display: inline-block;
		justify-self: center;
		max-width: 100%;
		max-height: min(62vh, calc(100dvh - 250px));
		overflow: hidden;
		touch-action: none;
		background: #050505;
		border-radius: 8px;
	}

	.target-stage img {
		display: block;
		width: auto;
		max-width: 100%;
		max-height: min(62vh, calc(100dvh - 250px));
		height: auto;
		object-fit: contain;
		user-select: none;
	}

	.target-overlay {
		position: absolute;
		top: 0;
		left: 0;
		width: 100%;
		height: 100%;
		pointer-events: none;
	}

	.target-status {
		position: absolute;
		inset: auto 1rem 1rem 1rem;
		padding: 0.55rem 0.75rem;
		text-align: center;
		border-radius: 8px;
		background: rgba(0, 0, 0, 0.72);
		color: #fff;
		font-weight: 700;
	}

	.target-message {
		color: #facc15;
		font-size: 0.9rem;
	}

	@media (max-width: 640px) {
		.target-controls {
			align-items: flex-start;
		}

		.brush-control {
			flex-basis: 100%;
		}

		.brush-control input {
			flex: 1;
			width: auto;
			min-width: 0;
		}
	}
</style>
